import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import type { CaseStudy } from '../types';

interface CaseStudyCardProps {
  caseStudy: CaseStudy;
  onViewDetails: (caseStudyId: string) => void;
}

const CaseStudyCard: React.FC<CaseStudyCardProps> = ({ caseStudy, onViewDetails }) => {
  return (
    <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden hover:shadow-lg transition-shadow duration-fast">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={caseStudy.image}
          alt={caseStudy.alt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4 bg-success text-white px-3 py-1 rounded-full text-xs font-medium">
          Success Story
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-text-primary mb-2">{caseStudy.clientName}</h3>
        <p className="text-sm text-text-secondary mb-4">{caseStudy.industry}</p>

        <div className="space-y-4 mb-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm font-medium text-text-primary">Challenge</span>
            </div>
            <p className="text-sm text-text-secondary line-clamp-2">{caseStudy.challenge}</p>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-text-primary">Solution</span>
            </div>
            <p className="text-sm text-text-secondary line-clamp-2">{caseStudy.solution}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {caseStudy.results.slice(0, 3).map((result, index) => (
            <div key={index} className="bg-surface rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-primary mb-1">{result.value}</div>
              <div className="text-xs text-text-secondary">{result.metric}</div>
            </div>
          ))}
        </div>

        <div className="bg-muted rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <Image
              src={caseStudy.testimonial.avatar}
              alt={caseStudy.testimonial.alt}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex-1">
              <p className="text-sm text-text-secondary italic mb-2 line-clamp-3">
                "{caseStudy.testimonial.quote}"
              </p>
              <div className="text-xs font-medium text-text-primary">
                {caseStudy.testimonial.author}
              </div>
              <div className="text-xs text-text-secondary">{caseStudy.testimonial.position}</div>
            </div>
          </div>
        </div>

        <button
          onClick={() => onViewDetails(caseStudy.id)}
          className="w-full flex items-center justify-center space-x-2 text-primary hover:text-primary/80 transition-colors duration-fast"
        >
          <span className="text-sm font-medium">View Full Case Study</span>
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
    </div>
  );
};

export default CaseStudyCard;